import { api } from '@/lib/axios';
import type { CurrentUser, UserRole } from '@/types';

/** DTO utilisateur renvoyé par le backend (snake_case). */
export interface UtilisateurDTO {
  id: string;
  nom: string;
  prenom: string;
  email: string;
  role: UserRole;
  avatar_url?: string | null;
  is_active: boolean;
  derniere_connexion?: string | null;
  created_at?: string;
  permissions?: Record<string, string[]> | null;
}

interface LoginResponseDTO {
  access_token: string;
  refresh_token: string;
  token_type: string;
  user: UtilisateurDTO;
}

/** Convertit un DTO utilisateur en modèle front. */
export function mapUser(dto: UtilisateurDTO): CurrentUser {
  return {
    id: dto.id,
    nom: dto.nom,
    prenom: dto.prenom,
    email: dto.email,
    role: dto.role,
    avatarUrl: dto.avatar_url ?? null,
    permissions: dto.permissions ?? null,
  };
}

export interface LoginResult {
  user: CurrentUser;
  accessToken: string;
  refreshToken: string;
}

/** Authentifie l'utilisateur (OAuth2 password flow, champ `username` = email). */
export async function login(email: string, password: string): Promise<LoginResult> {
  const form = new URLSearchParams();
  form.set('username', email);
  form.set('password', password);
  const { data } = await api.post<LoginResponseDTO>('/auth/login', form, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });
  return {
    user: mapUser(data.user),
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
  };
}

/** Récupère le profil de l'utilisateur authentifié. */
export async function fetchMe(): Promise<CurrentUser> {
  const { data } = await api.get<UtilisateurDTO>('/auth/me');
  return mapUser(data);
}

/** Déconnexion côté serveur (journalisation). */
export async function logout(): Promise<void> {
  await api.post('/auth/logout').catch(() => undefined);
}
